import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FiBriefcase, FiSearch, FiRefreshCcw, FiDownload } from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import Modal from '../../components/Modal/Modal'
import './HiringRequests.css'

const HiringRequests = () => {
  const [hiringRequests, setHiringRequests] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [showExportModal, setShowExportModal] = useState(false)

  useEffect(() => {
    loadHiringRequests()
  }, [page, statusFilter])

  const loadHiringRequests = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page,
        limit: 25,
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }
      const response = await adminApi.get('/hiring-requests', { params })
      setHiringRequests(response.data.data || response.data || [])
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load hiring requests')
      console.error('Error loading hiring requests:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await adminApi.patch(`/hiring-requests/${requestId}`, { status: newStatus })
      loadHiringRequests()
    } catch (err) {
      console.error('Status update failed:', err)
    }
  }

  const filteredRequests = useMemo(() => {
    const requestsList = hiringRequests || []
    if (!Array.isArray(requestsList)) return []
    const query = searchQuery.toLowerCase()
    return requestsList.filter((request) => {
      const matchesSearch =
        !query ||
        [
          request.companyName,
          request.contactName,
          request.email,
          request.phone,
          request.role,
          request.techStack,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter
      
      return matchesSearch && matchesStatus
    })
  }, [hiringRequests, searchQuery, statusFilter])

  const handleExport = async () => {
    try {
      const csvData = [
        ['Company', 'Contact', 'Email', 'Phone', 'Role', 'Hiring Type', 'Status', 'Date'].join(','),
        ...filteredRequests.map((r) =>
          [
            `"${r.companyName || ''}"`,
            `"${r.contactName || ''}"`,
            `"${r.email || ''}"`,
            `"${r.phone || ''}"`,
            `"${r.role || ''}"`,
            `"${r.hiringType || ''}"`,
            `"${r.status || ''}"`,
            `"${r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}"`,
          ].join(',')
        ),
      ].join('\n')

      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `hiring-requests-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setShowExportModal(false)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const columns = [
    {
      key: 'companyName',
      label: 'Company',
      render: (row) => (
        <div>
          <strong>{row.companyName}</strong>
          <span className="hiring-request-contact">{row.contactName}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Contact Info',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          {row.phone && <div className="hiring-request-phone">{row.phone}</div>}
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => row.role || '—',
    },
    {
      key: 'hiringType',
      label: 'Hiring Type',
      render: (row) => row.hiringType || '—',
    },
    {
      key: 'techStack',
      label: 'Tech Stack',
      render: (row) => (
        <div className="hiring-request-tech">
          {row.techStack?.substring(0, 50)}
          {row.techStack?.length > 50 && '...'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'new'} />,
    },
    {
      key: 'createdAt',
      label: 'Date',
      render: (row) => {
        if (!row.createdAt) return '—'
        return new Date(row.createdAt).toLocaleDateString()
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/hiring-requests/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="hiring-requests-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Hiring Management</p>
          <h1>Hiring Requests</h1>
          <p className="subhead">Manage hiring requests from companies</p>
        </div>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowExportModal(true)}
          >
            <FiDownload size={18} />
            Export
          </Button>
          <Button
            variant="secondary"
            onClick={loadHiringRequests}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search hiring requests"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'new', label: 'New' },
            { value: 'in_review', label: 'In Review' },
            { value: 'contacted', label: 'Contacted' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredRequests}
        loading={isLoading}
        emptyMessage="No hiring requests found"
      />

      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Hiring Requests"
        size="small"
      >
        <div className="export-options">
          <p>Export {filteredRequests.length} hiring request(s) to CSV file?</p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleExport}>
              <FiDownload size={18} />
              Export CSV
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default HiringRequests

